import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, FileText, MessageSquare, Link2, TrendingUp, Undo2 } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from "recharts";
import api from "@/lib/api";

/**
 * Dashboard som visar hur mycket AI-hjälpen används av handläggarna:
 *  - Formalisering av fältanteckningar (accept-rate)
 *  - Formalisering av handläggarkommentarer (accept-rate)
 *  - Auto-koppling av dokument (undo-rate = falska positiva)
 *  - Trend senaste 30d + topp-användare
 */
export default function AiStats() {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState(null);
  const [rangeDays, setRangeDays] = useState(30);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/ai/stats"),
          api.get(`/ai/stats/timeseries?days=${rangeDays}`),
        ]);
        setStats(s.data);
        setSeries(t.data.series || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Kunde inte hämta statistik");
      } finally {
        setLoading(false);
      }
    })();
  }, [rangeDays]);

  const legendFormatter = useCallback((value, entry) => {
    const isHidden = hiddenSeries.has(entry.dataKey);
    return (
      <span style={{ color: isHidden ? "#a1a1aa" : "#3f3f46", textDecoration: isHidden ? "line-through" : "none" }}>
        {value}
      </span>
    );
  }, [hiddenSeries]);

  if (loading) return (
    <div className="p-8 flex items-center gap-2 text-zinc-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Laddar AI-statistik…
    </div>
  );
  if (err) return <div className="p-8 text-red-600 text-sm">{err}</div>;
  if (!stats) return null;

  const notes = stats.notes_formalize;
  const comment = stats.comment_formalize;
  const match = stats.doc_auto_match;

  const toggleSeries = (key) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const resetSeries = () => setHiddenSeries(new Set());
  const showOnly = (keys) => {
    const all = ["notes_attempts", "notes_accepts", "comment_attempts", "comment_accepts", "match_created", "match_undone"];
    setHiddenSeries(new Set(all.filter((k) => !keys.includes(k))));
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="ai-stats-page">
      <header className="mb-2">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-purple-700" />
          <h1 className="text-3xl font-medium text-zinc-900" style={{ fontFamily: "Outfit" }}>
            AI-hjälpens statistik
          </h1>
        </div>
        <p className="text-sm text-zinc-500 max-w-2xl">
          Hur mycket AI-stödet används av handläggarna, hur ofta förslagen accepteras,
          och hur ofta auto-kopplingar behövde ångras. Data uppdateras realtid.
        </p>
      </header>

      {/* Top KPI-kort */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={<FileText className="w-4 h-4 text-purple-700" />}
          title="Fältanteckningar"
          value={`${notes.accept_rate_pct}%`}
          subtitle="Accept-rate"
          detail={`${notes.accepts} av ${notes.attempts} formaliseringar accepterade`}
          testid="kpi-notes"
          tone="purple"
        />
        <KpiCard
          icon={<MessageSquare className="w-4 h-4 text-purple-700" />}
          title="Kommentarer"
          value={`${comment.accept_rate_pct}%`}
          subtitle="Accept-rate"
          detail={`${comment.accepts} av ${comment.attempts} formaliseringar accepterade`}
          testid="kpi-comment"
          tone="purple"
        />
        <KpiCard
          icon={<Link2 className="w-4 h-4 text-emerald-700" />}
          title="Auto-koppling"
          value={`${100 - match.undo_rate_pct}%`}
          subtitle="Träffsäkerhet"
          detail={`${match.kept} kopplade · ${match.undone} ångrade av ${match.created}`}
          testid="kpi-match"
          tone="emerald"
        />
      </div>

      {/* Detaljerad breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownCard title="Formalisering (aktivitet totalt)" testid="breakdown-formalize">
          <BreakdownRow icon={<FileText className="w-3.5 h-3.5" />} label="Notes: attempts" value={notes.attempts} />
          <BreakdownRow label="Notes: accepts" value={notes.accepts} highlight="green" />
          <BreakdownRow label="Notes: errors" value={notes.errors} highlight={notes.errors > 0 ? "amber" : undefined} />
          <div className="border-t border-zinc-200 my-2" />
          <BreakdownRow icon={<MessageSquare className="w-3.5 h-3.5" />} label="Kommentarer: attempts" value={comment.attempts} />
          <BreakdownRow label="Kommentarer: accepts" value={comment.accepts} highlight="green" />
          <BreakdownRow label="Kommentarer: errors" value={comment.errors} highlight={comment.errors > 0 ? "amber" : undefined} />
        </BreakdownCard>

        <BreakdownCard title="Auto-koppling dokument → checklist" testid="breakdown-match">
          <BreakdownRow icon={<Link2 className="w-3.5 h-3.5" />} label="Skapade kopplingar" value={match.created} />
          <BreakdownRow label="Fortfarande giltiga" value={match.kept} highlight="green" />
          <BreakdownRow icon={<Undo2 className="w-3.5 h-3.5" />} label="Ångrade av handläggare" value={match.undone} highlight={match.undone > 0 ? "amber" : undefined} />
          <div className="mt-3 text-xs text-zinc-500">
            {match.undo_rate_pct}% behövde ångras – låg siffra = bra AI-precision.
          </div>
        </BreakdownCard>
      </div>

      {/* Trend-graf */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="trend-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-700" />
            <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>
              AI-användning per dag
            </h3>
          </div>
          <div className="inline-flex bg-zinc-100 rounded-sm p-0.5 border border-zinc-200" role="tablist">
            {[7, 30, 90].map((n) => (
              <button
                key={n}
                onClick={() => setRangeDays(n)}
                data-testid={`range-${n}d`}
                className={`px-3 py-1 text-xs font-medium rounded-sm transition ${
                  rangeDays === n ? "bg-white text-blue-700 shadow-sm" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                {n}d
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="series-presets">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 mr-1">Fokus:</span>
          <PresetBtn active={hiddenSeries.size === 0} onClick={resetSeries} testid="preset-all">Alla</PresetBtn>
          <PresetBtn
            active={hiddenSeries.size === 4 && !hiddenSeries.has("notes_attempts") && !hiddenSeries.has("notes_accepts")}
            onClick={() => showOnly(["notes_attempts", "notes_accepts"])}
            testid="preset-notes"
          >Fältanteckningar</PresetBtn>
          <PresetBtn
            active={hiddenSeries.size === 4 && !hiddenSeries.has("comment_attempts") && !hiddenSeries.has("comment_accepts")}
            onClick={() => showOnly(["comment_attempts", "comment_accepts"])}
            testid="preset-comments"
          >Kommentarer</PresetBtn>
          <PresetBtn
            active={hiddenSeries.size === 4 && !hiddenSeries.has("match_created") && !hiddenSeries.has("match_undone")}
            onClick={() => showOnly(["match_created", "match_undone"])}
            testid="preset-match"
          >Auto-koppling</PresetBtn>
        </div>

        <MiniTotalsBar series={series} hidden={hiddenSeries} onToggle={toggleSeries} />

        <div className="h-80 w-full mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#f4f4f5" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "#71717a" }}
                tickFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
                }}
                interval={Math.max(0, Math.floor(rangeDays / 10))}
              />
              <YAxis tick={{ fontSize: 11, fill: "#71717a" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", fontSize: 12, borderRadius: 2 }}
                labelFormatter={(v) => new Date(v).toLocaleDateString("sv-SE", { weekday: "short", day: "numeric", month: "short" })}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8, cursor: "pointer" }}
                onClick={(e) => toggleSeries(e.dataKey)}
                formatter={legendFormatter}
              />
              <Line type="monotone" dataKey="notes_attempts" name="Notes – försök" stroke="#a855f7" strokeWidth={2} dot={false} hide={hiddenSeries.has("notes_attempts")} />
              <Line type="monotone" dataKey="notes_accepts" name="Notes – accepterade" stroke="#7e22ce" strokeWidth={2} strokeDasharray="4 4" dot={false} hide={hiddenSeries.has("notes_accepts")} />
              <Line type="monotone" dataKey="comment_attempts" name="Kommentar – försök" stroke="#0284c7" strokeWidth={2} dot={false} hide={hiddenSeries.has("comment_attempts")} />
              <Line type="monotone" dataKey="comment_accepts" name="Kommentar – accepterade" stroke="#075985" strokeWidth={2} strokeDasharray="4 4" dot={false} hide={hiddenSeries.has("comment_accepts")} />
              <Line type="monotone" dataKey="match_created" name="Auto-koppling – skapade" stroke="#059669" strokeWidth={2} dot={false} hide={hiddenSeries.has("match_created")} />
              <Line type="monotone" dataKey="match_undone" name="Auto-koppling – ångrade" stroke="#dc2626" strokeWidth={2} strokeDasharray="4 4" dot={false} hide={hiddenSeries.has("match_undone")} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">
          Streckade linjer = accepterade/ångrade händelser. Klicka i legenden eller totals-baren för att dölja/visa en linje.
        </p>
      </div>

      {/* Topp användare */}
      {stats.top_users_30d?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="top-users">
          <h3 className="text-lg font-medium mb-3" style={{ fontFamily: "Outfit" }}>
            Mest aktiva handläggare (30d)
          </h3>
          <ol className="space-y-2">
            {stats.top_users_30d.map((u, i) => (
              <li key={u.name} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="flex-1 text-sm text-zinc-900">{u.name}</span>
                <span className="text-xs text-zinc-500">{u.events} AI-händelser</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}


function KpiCard({ icon, title, value, subtitle, detail, testid, tone = "purple" }) {
  const toneClasses = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50/40"
    : "border-purple-200 bg-purple-50/40";
  return (
    <div className={`border ${toneClasses} rounded-sm p-5`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500 mb-2">
        {icon} {title}
      </div>
      <div className="text-4xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{subtitle}</div>
      <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{detail}</p>
    </div>
  );
}


function BreakdownCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}


function BreakdownRow({ icon, label, value, highlight }) {
  const bg = highlight === "green" ? "bg-emerald-50 text-emerald-800"
    : highlight === "amber" ? "bg-amber-50 text-amber-800"
    : "text-zinc-900";
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="flex items-center gap-1.5 text-zinc-700">{icon}{label}</span>
      <span className={`px-2 py-0.5 rounded-sm font-mono ${bg}`}>{value}</span>
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
      <div className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}


function PresetBtn({ children, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 text-xs rounded-sm border transition ${
        active ? "bg-blue-700 text-white border-blue-700" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      {children}
    </button>
  );
}


function MiniTotalsBar({ series, hidden, onToggle }) {
  if (!series || series.length === 0) return null;
  const totals = series.reduce(
    (acc, r) => ({
      notes_attempts: acc.notes_attempts + (r.notes_attempts || 0),
      notes_accepts: acc.notes_accepts + (r.notes_accepts || 0),
      comment_attempts: acc.comment_attempts + (r.comment_attempts || 0),
      comment_accepts: acc.comment_accepts + (r.comment_accepts || 0),
      match_created: acc.match_created + (r.match_created || 0),
      match_undone: acc.match_undone + (r.match_undone || 0),
    }),
    { notes_attempts: 0, notes_accepts: 0, comment_attempts: 0, comment_accepts: 0, match_created: 0, match_undone: 0 },
  );
  const items = [
    { key: "notes_attempts", label: "Notes-försök", value: totals.notes_attempts, dot: "#a855f7" },
    { key: "notes_accepts", label: "Notes-accept", value: totals.notes_accepts, dot: "#7e22ce" },
    { key: "comment_attempts", label: "Kommentar-försök", value: totals.comment_attempts, dot: "#0284c7" },
    { key: "comment_accepts", label: "Kommentar-accept", value: totals.comment_accepts, dot: "#075985" },
    { key: "match_created", label: "Auto-koppl.", value: totals.match_created, dot: "#059669" },
    { key: "match_undone", label: "Ångrade", value: totals.match_undone, dot: "#dc2626" },
  ];
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 pb-3 border-b border-zinc-100" data-testid="totals-bar">
      {items.map((it) => {
        const isHidden = hidden?.has(it.key);
        return (
          <button
            key={it.key}
            onClick={() => onToggle?.(it.key)}
            data-testid={`toggle-${it.key}`}
            title={isHidden ? "Visa i grafen" : "Dölj från grafen"}
            className={`flex items-center gap-1.5 rounded-sm px-1.5 py-1 text-left transition ${
              isHidden ? "opacity-40" : "hover:bg-zinc-50"
            }`}
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: it.dot }} />
            <div className="min-w-0">
              <div className={`text-lg font-semibold leading-tight ${isHidden ? "line-through text-zinc-500" : "text-zinc-900"}`} style={{ fontFamily: "Outfit" }}>
                {it.value}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{it.label}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
